import "core-js/modules/es.array.concat.js";
import http from '@/api/http';
var BASE_URL = 'api/v5/cs';

/**
 * 客服系统API
 */
export default {
  methods: {
    // ==================== 系统设置与监控 ====================
    /**
     * 获取客服系统设置
     * @returns {Promise<{basic:Object, transfer:Object, transferKeywords:Array}>}
     */
    getSystemSettings: function getSystemSettings() {
      return http.get("".concat(BASE_URL, "/config"));
    },
    /**
     * 更新客服系统设置
     * @param {('basic'|'transfer')} section 配置分段
     * @param {Object} data 配置数据
     * @returns {Promise}
     */
    updateSystemSettings: function updateSystemSettings(section, data) {
      return http.post("".concat(BASE_URL, "/config/").concat(section), data);
    },
    /**
     * 获取系统运行状态
     * @returns {Promise<{activeSessions:number, onlineAgents:number, queueLength:number, avgResponseTime:number}>}
     */
    getSystemStatus: function getSystemStatus() {
      return http.get("".concat(BASE_URL, "/system/status"));
    },
    // ==================== 会话管理 ====================
    /**
     * 创建客服会话
     * @param {Object} data 创建会话请求数据
     * @returns {Promise}
     */
    createSession: function createSession(data) {
      return http.post("".concat(BASE_URL, "/sessions"), data);
    },
    /**
     * 获取会话详情
     * @param {string} sessionId 会话ID
     * @returns {Promise}
     */
    getSession: function getSession(sessionId) {
      return http.get("".concat(BASE_URL, "/sessions/").concat(sessionId));
    },
    /**
     * 获取活跃会话列表
     * @param {number} agentId 客服人员ID（可选）
     * @returns {Promise}
     */
    getActiveSessions: function getActiveSessions() {
      var agentId = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : null;
      return http.get("".concat(BASE_URL, "/sessions"), {
        params: agentId ? {
          agentId: agentId
        } : {}
      });
    },
    /**
     * 结束会话
     * @param {string} sessionId 会话ID
     * @returns {Promise}
     */
    endSession: function endSession(sessionId) {
      return http.post("".concat(BASE_URL, "/sessions/").concat(sessionId, "/end"));
    },
    /**
     * 设置会话满意度
     * @param {string} sessionId 会话ID
     * @param {number} score 满意度评分
     * @param {string} feedback 反馈内容
     * @returns {Promise}
     */
    setSessionSatisfaction: function setSessionSatisfaction(sessionId, score) {
      var feedback = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : '';
      return http.post("".concat(BASE_URL, "/sessions/").concat(sessionId, "/satisfaction"), null, {
        params: {
          score: score,
          feedback: feedback
        }
      });
    },
    // ==================== 消息管理 ====================
    /**
     * 获取会话消息列表
     * @param {string} sessionId 会话ID
     * @param {number} page 页码
     * @param {number} size 页大小
     * @returns {Promise}
     */
    getMessages: function getMessages(sessionId) {
      var page = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 1;
      var size = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 50;
      return http.get("".concat(BASE_URL, "/messages/").concat(sessionId, "/messages"), {
        page: page,
        size: size
      });
    },
    /**
     * 发送消息
     * @param {Object} data 发送消息请求数据
     * @returns {Promise}
     */
    sendMessage: function sendMessage(data) {
      return http.post("".concat(BASE_URL, "/messages"), data);
    },
    /**
     * 标记消息为已读
     * @param {Array} messageIds 消息ID列表
     * @returns {Promise}
     */
    markMessagesAsRead: function markMessagesAsRead(messageIds) {
      return http.post("".concat(BASE_URL, "/messages/mark-read"), messageIds);
    },
    /**
     * 获取未读消息数
     * @param {string} sessionId 会话ID
     * @param {number} agentId 客服人员ID
     * @returns {Promise}
     */
    getUnreadCount: function getUnreadCount(sessionId) {
      var agentId = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
      return http.get("".concat(BASE_URL, "/messages/").concat(sessionId, "/unread-count"), {
        agentId: agentId
      });
    },
    /**
     * 触发语音消息转文字
     * @param {string} messageId 消息ID
     * @returns {Promise}
     */
    transcribeAudioMessage: function transcribeAudioMessage(messageId) {
      return http.post("".concat(BASE_URL, "/messages/").concat(messageId, "/transcript"));
    },
    /**
     * 触发或重试语音转码
     * @param {string} messageId 消息ID
     * @param {boolean} wait 是否等待转码完成
     * @returns {Promise}
     */
    convertAudioMessage: function convertAudioMessage(messageId) {
      var wait = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
      return http.post("".concat(BASE_URL, "/messages/").concat(messageId, "/audio/convert"), null, {
        params: {
          wait: wait
        }
      });
    },
    // ==================== 客服管理 ====================
    /**
     * 获取客服人员列表
     * @returns {Promise}
     */
    getAllAgents: function getAllAgents() {
      return http.get("".concat(BASE_URL, "/agent/all"));
    },
    /**
     * 获取在线客服列表
     * @returns {Promise}
     */
    getOnlineAgents: function getOnlineAgents() {
      return http.get("".concat(BASE_URL, "/agent/online"));
    },
    /**
     * 创建客服人员
     * @param {number} userId 用户ID
     * @param {string} agentCode 客服工号
     * @param {string} agentName 客服姓名
     * @param {string} department 部门
     * @param {number} maxConcurrentSessions 最大并发会话数
     * @returns {Promise}
     */
    createAgent: function createAgent(userId, agentCode, agentName, department) {
      var maxConcurrentSessions = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : 5;
      return http.post("".concat(BASE_URL, "/agent"), {
        userId: userId,
        agentCode: agentCode,
        agentName: agentName,
        department: department,
        maxConcurrentSessions: maxConcurrentSessions
      });
    },
    /**
     * 更新客服信息
     * @param {number} agentId 客服人员ID
     * @param {string} agentName 客服姓名
     * @param {string} department 部门
     * @param {number} maxConcurrentSessions 最大并发会话数
     * @returns {Promise}
     */
    updateAgent: function updateAgent(agentId, agentName, department, maxConcurrentSessions) {
      return http.put("".concat(BASE_URL, "/agent/").concat(agentId), {
        agentName: agentName,
        department: department,
        maxConcurrentSessions: maxConcurrentSessions
      });
    },
    /**
     * 启用/禁用客服人员
     * @param {number} agentId 客服人员ID
     * @param {boolean} isActive 是否启用
     * @returns {Promise}
     */
    setAgentActive: function setAgentActive(agentId, isActive) {
      return http.post("".concat(BASE_URL, "/agent/").concat(agentId, "/active"), null, {
        params: {
          isActive: isActive
        }
      });
    },
    /**
     * 更新客服状态
     * @param {number} agentId 客服人员ID
     * @param {number} status 状态
     * @returns {Promise}
     */
    updateAgentStatus: function updateAgentStatus(agentId, status) {
      return http.post("".concat(BASE_URL, "/agent/").concat(agentId, "/status"), null, {
        params: {
          status: status
        }
      });
    },
    /**
     * 获取客服工作统计
     * @param {number} agentId 客服人员ID
     * @param {Date} startDate 开始日期
     * @param {Date} endDate 结束日期
     * @returns {Promise}
     */
    getAgentStatistics: function getAgentStatistics(agentId, startDate, endDate) {
      return http.get("".concat(BASE_URL, "/agent/").concat(agentId, "/statistics"), {
        agentId: agentId,
        beginAt: startDate.toISOString(),
        endAt: endDate.toISOString()
      });
    },
    // ==================== 统计报表 ====================
    /**
     * 获取会话统计
     * @param {Date} startDate 开始日期
     * @param {Date} endDate 结束日期
     * @param {number} agentId 客服人员ID（可选）
     * @returns {Promise}
     */
    getSessionStatistics: function getSessionStatistics(startDate, endDate) {
      var agentId = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : null;
      var params = {
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString()
      };
      if (agentId) {
        params.agentId = agentId;
      }
      return http.get("".concat(BASE_URL, "/statistics/sessions"), params);
    },
    // ==================== 知识库管理 ====================
    /**
     * 获取知识库分类列表
     * @returns {Promise}
     */
    getKbCategories: function getKbCategories() {
      return http.get("".concat(BASE_URL, "/kb/categories"));
    },
    /**
     * 创建知识库分类
     * @param {Object} data 分类数据
     * @returns {Promise}
     */
    createKbCategory: function createKbCategory(data) {
      return http.post("".concat(BASE_URL, "/kb/categories"), data);
    },
    /**
     * 更新知识库分类
     * @param {number} categoryId 分类ID
     * @param {Object} data 分类数据
     * @returns {Promise}
     */
    updateKbCategory: function updateKbCategory(categoryId, data) {
      return http.put("".concat(BASE_URL, "/kb/categories/").concat(categoryId), data);
    },
    /**
     * 删除知识库分类
     * @param {number} categoryId 分类ID
     * @returns {Promise}
     */
    deleteKbCategory: function deleteKbCategory(categoryId) {
      return http.del("".concat(BASE_URL, "/kb/categories/").concat(categoryId));
    },
    /**
     * 获取知识库文档列表
     * @param {Object} params 查询参数
     * @returns {Promise}
     */
    getKbDocuments: function getKbDocuments() {
      var params = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
      return http.get("".concat(BASE_URL, "/kb/documents"), params);
    },
    /**
     * 创建知识库文档
     * @param {Object} data 文档数据
     * @returns {Promise}
     */
    createKbDocument: function createKbDocument(data) {
      return http.post("".concat(BASE_URL, "/kb/documents"), data);
    },
    /**
     * 更新知识库文档
     * @param {string} documentId 文档ID
     * @param {Object} data 文档数据
     * @returns {Promise}
     */
    updateKbDocument: function updateKbDocument(documentId, data) {
      return http.put("".concat(BASE_URL, "/kb/documents/").concat(documentId), data);
    },
    /**
     * 删除知识库文档
     * @param {string} documentId 文档ID
     * @returns {Promise}
     */
    deleteKbDocument: function deleteKbDocument(documentId) {
      return http.del("".concat(BASE_URL, "/kb/documents/").concat(documentId));
    },
    /**
     * 触发文档向量化
     * @param {string} documentId 文档ID
     * @returns {Promise}
     */
    vectorizeDocument: function vectorizeDocument(documentId) {
      return http.post("".concat(BASE_URL, "/kb/documents/").concat(documentId, "/vectorize"));
    },
    /**
     * 批量向量化文档
     * @param {Array} documentIds 文档ID列表
     * @returns {Promise}
     */
    batchVectorizeDocuments: function batchVectorizeDocuments(documentIds) {
      return http.post("".concat(BASE_URL, "/kb/documents/batch-vectorize"), documentIds);
    },
    /**
     * 搜索知识库
     * @param {string} query 搜索关键词
     * @param {number} limit 返回数量限制
     * @returns {Promise}
     */
    searchKnowledge: function searchKnowledge(query) {
      var limit = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 5;
      return http.get("".concat(BASE_URL, "/kb/search"), {
        query: query,
        limit: limit
      });
    },
    /**
     * 获取知识库统计信息
     * @returns {Promise}
     */
    getKnowledgeStats: function getKnowledgeStats() {
      return http.get("".concat(BASE_URL, "/kb/stats"));
    },
    // ==================== 人工回复话术 ====================
    /**
     * 获取话术分类
     * @param {Object} params 查询参数
     * @returns {Promise}
     */
    getReplyTemplateCategories: function getReplyTemplateCategories() {
      var params = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
      return http.get("".concat(BASE_URL, "/reply-template/categories"), params);
    },
    /**
     * 创建话术分类
     * @param {Object} data 分类数据
     * @returns {Promise}
     */
    createReplyTemplateCategory: function createReplyTemplateCategory(data) {
      return http.post("".concat(BASE_URL, "/reply-template/categories"), data);
    },
    /**
     * 更新话术分类
     * @param {number} id 分类ID
     * @param {Object} data 分类数据
     * @returns {Promise}
     */
    updateReplyTemplateCategory: function updateReplyTemplateCategory(id, data) {
      return http.put("".concat(BASE_URL, "/reply-template/categories/").concat(id), data);
    },
    /**
     * 删除话术分类
     * @param {number} id 分类ID
     * @returns {Promise}
     */
    deleteReplyTemplateCategory: function deleteReplyTemplateCategory(id) {
      return http.destroy("".concat(BASE_URL, "/reply-template/categories/").concat(id));
    },
    /**
     * 获取话术列表
     * @param {Object} params 查询参数
     * @returns {Promise}
     */
    getReplyTemplates: function getReplyTemplates() {
      var params = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
      return http.get("".concat(BASE_URL, "/reply-template"), params);
    },
    /**
     * 创建话术
     * @param {Object} data 话术数据
     * @returns {Promise}
     */
    createReplyTemplate: function createReplyTemplate(data) {
      return http.post("".concat(BASE_URL, "/reply-template"), data);
    },
    /**
     * 更新话术
     * @param {number} id 话术ID
     * @param {Object} data 话术数据
     * @returns {Promise}
     */
    updateReplyTemplate: function updateReplyTemplate(id, data) {
      return http.put("".concat(BASE_URL, "/reply-template/").concat(id), data);
    },
    /**
     * 删除话术
     * @param {number} id 话术ID
     * @returns {Promise}
     */
    deleteReplyTemplate: function deleteReplyTemplate(id) {
      return http.destroy("".concat(BASE_URL, "/reply-template/").concat(id));
    },
    /**
     * 记录话术使用次数
     * @param {number} id 话术ID
     * @returns {Promise}
     */
    useReplyTemplate: function useReplyTemplate(id) {
      return http.post("".concat(BASE_URL, "/reply-template/").concat(id, "/use"));
    },
    // ==================== 会话质控 ====================
    /**
     * 获取质控会话列表
     * @param {Object} params 查询参数
     * @returns {Promise}
     */
    getQualitySessions: function getQualitySessions() {
      var params = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
      return http.get("".concat(BASE_URL, "/quality/sessions"), params);
    },
    /**
     * 获取质控会话详情
     * @param {string} sessionId 会话ID
     * @returns {Promise}
     */
    getQualitySessionDetail: function getQualitySessionDetail(sessionId) {
      return http.get("".concat(BASE_URL, "/quality/sessions/").concat(sessionId));
    },
    /**
     * 保存会话质检
     * @param {string} sessionId 会话ID
     * @param {Object} data 质检数据
     * @returns {Promise}
     */
    reviewQualitySession: function reviewQualitySession(sessionId, data) {
      return http.post("".concat(BASE_URL, "/quality/sessions/").concat(sessionId, "/review"), data);
    },
    /**
     * 获取质控统计
     * @param {Object} params 查询参数
     * @returns {Promise}
     */
    getQualityStatistics: function getQualityStatistics() {
      var params = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
      return http.get("".concat(BASE_URL, "/quality/statistics"), params);
    }
  }
};